import { localizedStrings } from '@core/strings'
import { Palette } from '@core/theme/color-palette-siteassist'
import React, { useMemo } from 'react'
import {
  Breadcrumbs as MaterialBreadcrumbs,
  Link as MaterialLink,
} from '@common/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useNavigationContext } from '@common/hooks'
import { PageUrls } from '@core/page-urls'
import { useGetPermitTemplate } from '@core/react-query/features/templates/permitTemplates/permitTemplate'
import { useGetOrganisation } from '@core/react-query/features/organisations/organisation/hooks/useGetOrganisation'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { useGetBusinessUnit } from '@core/react-query/features/businessUnits'
import { useGetProject } from '@core/react-query/features/projects/project/hooks/useGetProject'
import {
  useGetChecklistTemplate,
  useGetChecklistTemplateVersion,
} from '@core/react-query/features/templates/checklistTemplates'
import { usePermitById } from '@core/react-query/features/permits/permit'
import { useGetPermitTemplateVersion } from '@core/react-query/features/templates/permitTemplateEditor'
import { useGetAuthGroup } from '@core/react-query/features/authGroups/hooks/useGetAuthGroup'
import { useGetChecklistById } from '@core/react-query/features/checklists/checklist/hooks/useGetChecklistById'
import { useGetTaskById } from '@core/react-query/features/tasks/task/hooks/useGetTaskById'
import { useGetTaskTemplate } from '@core/react-query/features/tasks/templates'
import { useOrganisationUser } from '@core/react-query/features/organisations/organisation/hooks/useOrganisationUser'
import { useGetDocumentEngineTemplate } from '@core/react-query/features/templates/documentEngineTemplates/documentEngineTemplate'
import { useGetCompanyDetails } from '@core/react-query/features/company'
import {
  useGetCompetencyTemplateById,
  useGetCompetencyTemplateVersion,
} from '@core/react-query/features/templates/competencyTemplates'

export const NavigationAwareBreadcrumbs: React.FC<{}> = () => {
  const {
    organisationId,
    projectId,
    permitId,
    templateId,
    businessUnitId,
    templateVersionId,
    checklistTemplateVersionId,
    checklistTemplateId,
    checklistId,
    taskId,
    userId,
    authorisationGroupId,
    inTimelineContext,
    inTasksContext,
    inObservationsContext,
    inUsersContext,
    inAuthorisationGroupsContext,
    inCompaniesContext,
    inOrganisationCompaniesContext,
    companyId,
    inPermitTemplatesContext,
    inChecklistsContext,
    inInspectionsContext,
    inRegistersContext,
    inSettingsContext,
    inChecklistTemplatesContext,
    inInspectionTemplatesContext,
    inRegisterTemplateContext,
    inTaskTemplateContext,
    inTagsContext,
    inSiteLocationsContext,
    inMapContext,
    inProjectMapContext,
    inPlansContext,
    inSiteBoxContext,
    inSupportContext,
    asPathWithoutQuery,
    adminUserId,
    inProjectContext,
    inBusinessUnitContext,
    inAnalyticsContext,
    inPermitContext,
    inOrganisationHome,
    taskTemplateId,
    documentEngineTemplateId,
    inDocumentEngineTemplateContext,
    inCompetencyTemplatesContext,
    competencyTemplateId,
    competencyTemplateVersionId,
  } = useNavigationContext()

  const { data: businessUnit } = useGetBusinessUnit(
    { businessUnitId },
    { enabled: !!businessUnitId },
  )

  const { data: profile } = useGetProfile()
  const { data: organisation } = useGetOrganisation({ organisationId })
  const { data: project } = useGetProject(
    { projectId },
    { enabled: !!projectId && inProjectContext },
  )
  const { data: permit } = usePermitById({ permitId }, { enabled: !!permitId })
  const router = useRouter()
  const { data: checklist } = useGetChecklistById(
    { checklistId },
    { enabled: !!checklistId },
  )
  const { data: task } = useGetTaskById({ taskId }, { enabled: !!taskId })

  const { data: template } = useGetPermitTemplate({
    id: templateId,
  })

  const { data: permitTemplateVersion } = useGetPermitTemplateVersion(
    {
      templateId: templateId,
      versionId: templateVersionId,
    },
    {
      enabled: !!templateId && !!templateVersionId,
    },
  )
  const { data: checklistTemplate } = useGetChecklistTemplate(
    {
      templateId: checklistTemplateId,
    },
    {
      enabled: !!checklistTemplateId,
    },
  )

  const { data: checklistTemplateVersion } = useGetChecklistTemplateVersion(
    {
      templateId: checklistTemplateId,
      templateVersionId: checklistTemplateVersionId,
    },
    {
      enabled: !!checklistTemplateId && !!checklistTemplateVersionId,
    },
  )

  const { data: taskTemplate } = useGetTaskTemplate(
    { templateId: taskTemplateId },
    { enabled: !!taskTemplateId },
  )

  const { data: authGroup } = useGetAuthGroup(
    { groupId: authorisationGroupId },
    {
      enabled: !!authorisationGroupId,
    },
  )

  const { data: companyDetail } = useGetCompanyDetails(
    { organisationId, companyId },
    {
      enabled:
        !!organisationId && !!companyId && inOrganisationCompaniesContext,
    },
  )

  const { data: organisationUser } = useOrganisationUser(
    { organisationId, userId },
    {
      enabled: !!organisationId && !!userId && inUsersContext,
    },
  )

  const { data: documentEngineTemplate } = useGetDocumentEngineTemplate(
    {
      organisationId,
      templateId: documentEngineTemplateId,
    },
    {
      enabled: !!documentEngineTemplateId && !!organisationId,
    },
  )

  const { data: competencyTemplate } = useGetCompetencyTemplateById(
    { templateId: competencyTemplateId },
    { enabled: !!competencyTemplateId },
  )

  const { data: competencyTemplateVersion } = useGetCompetencyTemplateVersion(
    {
      templateId: competencyTemplateId,
      versionId: competencyTemplateVersionId,
    },
    {
      enabled: !!competencyTemplateId && !!competencyTemplateVersionId,
    },
  )

  const breadCrumbs = useMemo(() => {
    const crumbs: { href: string; label: string; disableLink?: boolean }[] = []

    if (inOrganisationHome) {
      crumbs.push({
        href: PageUrls.organisation(organisationId),
        label: localizedStrings.home,
      })
    }

    if (inBusinessUnitContext && !businessUnitId) {
      crumbs.push({
        href: PageUrls.businessUnits(organisationId),
        label: localizedStrings.businessUnits,
      })
    }

    if (businessUnit?.name) {
      crumbs.push({
        href: PageUrls.businessUnitOverview({
          id: organisationId,
          businessUnitId: businessUnitId,
        }),
        label: businessUnit?.name,
      })
    }

    if (inProjectContext && !projectId) {
      crumbs.push({
        href: PageUrls.projects(organisationId),
        label: localizedStrings.projects,
      })
    }

    if (inProjectContext && project?.name) {
      crumbs.push({
        href: PageUrls.projectOverview({
          organisationId: organisationId,
          projectId: projectId,
        }),
        label: project.name,
      })
    }

    if (inAnalyticsContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectAnalytics({
              organisationId: organisationId,
              projectId: projectId,
            })
          : PageUrls.organisationAnalytics({
              organisationId: organisationId,
            }),
        label: 'Analytics',
      })
    }

    if (inTimelineContext) {
      crumbs.push({
        href: PageUrls.organisationTimeline({
          organisationId: organisationId,
        }),
        label: localizedStrings.timeline,
      })
    }

    if (inTasksContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectTasks({ organisationId, projectId })
          : PageUrls.tasks(organisationId),
        label: localizedStrings.tasks,
      })
    }

    if (taskId && task?.template?.name) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectTaskDetail({
              organisationId: organisationId,
              projectId: projectId,
              taskId: taskId,
            })
          : PageUrls.task(organisationId, taskId),
        label: task.template.name,
      })
    }

    if (inObservationsContext && inProjectContext && !inSettingsContext) {
      crumbs.push({
        href: PageUrls.projectObservations({ organisationId, projectId }),
        label: localizedStrings.observations,
      })
    }

    if (inPermitContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectPermits(organisationId, projectId)
          : PageUrls.permits(organisationId),
        label: localizedStrings.permits,
      })
    }

    if (permitId && permit?.template?.name) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectPermitDetail({
              organisationId: organisationId,
              projectId: projectId,
              permitId: permitId,
            })
          : PageUrls.permit(organisationId, permitId),
        label: permit.template.name,
      })
    }

    if (inSettingsContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectSettings({ organisationId, projectId })
          : PageUrls.organisationSettings({ organisationId }),
        label: localizedStrings.settings,
      })
    }

    if (inSettingsContext && inObservationsContext && inProjectContext) {
      crumbs.push({
        href: PageUrls.projectObservationSettings({
          organisationId,
          projectId,
        }),
        label: localizedStrings.observations,
      })
      crumbs.push({
        href: `${PageUrls.projectObservationSettings({
          organisationId,
          projectId,
        })}?stage=general`,
        label: localizedStrings.generalSettings,
      })
    }

    if (inUsersContext) {
      const canViewOrganisationUsers =
        profile?.isSuperUser() ||
        profile?.isAccountOwnerOrViewer(organisationId)
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectUsers(organisationId, projectId)
          : PageUrls.organisationUsers(organisationId),
        label: localizedStrings.users,
        disableLink: !inProjectContext && !canViewOrganisationUsers,
      })
    }

    if (userId && organisationUser?.getFullName()) {
      crumbs.push({
        href: PageUrls.organisationUserProfile({
          organisationId,
          userId,
        }),
        label: organisationUser.getFullName(),
      })
    }

    // Inspections are created and drafted on the /checklists routes
    const inspectionOnChecklistsRoute =
      inChecklistsContext &&
      (router.query.isUsedForInspections === 'true' ||
        !!checklist?.isInspection)

    if (inChecklistsContext && !inspectionOnChecklistsRoute) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectChecklists({ organisationId, projectId })
          : PageUrls.checklists(organisationId),
        label: localizedStrings.checklists,
      })
    }

    if (inInspectionsContext || inspectionOnChecklistsRoute) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectInspections({ organisationId, projectId })
          : PageUrls.inspections(organisationId),
        label: localizedStrings.inspections,
      })
    }

    if (checklistId && checklist?.template?.name) {
      const isInspection = inInspectionsContext || !!checklist.isInspection
      const detailProjectId = inProjectContext
        ? projectId
        : checklist.project?.id
      const inspectionUrl = detailProjectId
        ? PageUrls.projectInspection({
            organisationId,
            projectId: detailProjectId,
            checklistId,
          })
        : PageUrls.inspection(organisationId, checklistId)
      const checklistUrl = detailProjectId
        ? PageUrls.projectChecklistDetail({
            organisationId,
            projectId: detailProjectId,
            checklistId,
          })
        : PageUrls.checklist(organisationId, checklistId)
      crumbs.push({
        href: isInspection ? inspectionUrl : checklistUrl,
        label: checklist.template.name,
      })
    }

    if (inRegistersContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectRegisters(organisationId, projectId)
          : PageUrls.registers(organisationId),
        label: localizedStrings.registers,
      })
    }

    if (inAuthorisationGroupsContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectAuthorisationGroupList({
              organisationId,
              projectId,
            })
          : PageUrls.organisationAuthorisationGroupList({ organisationId }),
        label: localizedStrings.authorisationGroups,
      })
    }

    if (inCompaniesContext) {
      crumbs.push({
        href: PageUrls.organisationCompaniesSettings({
          organisationId: organisationId,
        }),
        label: localizedStrings.companies,
      })
    }

    if (inOrganisationCompaniesContext) {
      crumbs.push({
        href: PageUrls.organisationCompanies({ organisationId }),
        label: localizedStrings.companies,
      })
    }

    if (companyId && companyDetail?.name && inOrganisationCompaniesContext) {
      crumbs.push({
        href: PageUrls.organisationCompanyDetail({
          organisationId,
          companyId,
        }),
        label: companyDetail.name,
      })
    }

    if (inPermitTemplatesContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectPermitTemplates({ organisationId, projectId })
          : PageUrls.organisationPermitTemplates({ organisationId }),
        label: localizedStrings.permitTemplates,
      })
    }

    if (templateId === template?.id && template?.name) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectPermitTemplates({
              organisationId: organisationId,
              projectId: projectId,
            })
          : PageUrls.editTemplate(organisationId, templateId),
        label: template?.name,
      })
    }

    if (permitTemplateVersion?.version) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectViewTemplateVersion({
              orgId: organisationId,
              projectId: projectId,
              templateId: templateId,
              versionId: templateVersionId,
            })
          : PageUrls.editTemplateVersion({
              orgId: organisationId,
              templateId: templateId,
              versionId: templateVersionId,
            }),
        label: `V${permitTemplateVersion?.version}`,
      })
    }

    if (inChecklistTemplatesContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectChecklistTemplates({ organisationId, projectId })
          : PageUrls.organisationChecklistTemplates({ organisationId }),
        label: localizedStrings.checklistTemplates,
      })
    }

    if (inInspectionTemplatesContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectInspectionTemplates({ organisationId, projectId })
          : PageUrls.organisationInspectionTemplates({ organisationId }),
        label: localizedStrings.inspectionTemplates,
      })
    }

    if (checklistTemplate?.name) {
      crumbs.push({
        href: inProjectContext
          ? inInspectionTemplatesContext
            ? PageUrls.projectViewInspectionTemplateVersion({
                orgId: organisationId,
                projectId: projectId,
                templateId: checklistTemplateId,
                versionId: checklistTemplateVersionId,
              })
            : PageUrls.projectViewChecklistTemplateVersion({
                orgId: organisationId,
                projectId: projectId,
                templateId: checklistTemplateId,
                versionId: checklistTemplateVersionId,
              })
          : inInspectionTemplatesContext
          ? PageUrls.editInspectionTemplate(organisationId, checklistTemplateId)
          : PageUrls.editChecklistTemplate(organisationId, checklistTemplateId),
        label: checklistTemplate?.name,
      })
    }

    if (checklistTemplateVersion?.version) {
      crumbs.push({
        href: inProjectContext
          ? inInspectionTemplatesContext
            ? PageUrls.projectViewInspectionTemplateVersion({
                orgId: organisationId,
                projectId: projectId,
                templateId: checklistTemplateId,
                versionId: checklistTemplateVersionId,
              })
            : PageUrls.projectViewChecklistTemplateVersion({
                orgId: organisationId,
                projectId: projectId,
                templateId: checklistTemplateId,
                versionId: checklistTemplateVersionId,
              })
          : inInspectionTemplatesContext
          ? PageUrls.editInspectionTemplateVersion({
              orgId: organisationId,
              templateId: checklistTemplateId,
              versionId: checklistTemplateVersionId,
            })
          : PageUrls.editChecklistTemplateVersion({
              orgId: organisationId,
              templateId: checklistTemplateId,
              versionId: checklistTemplateVersionId,
            }),
        label: `${checklistTemplateVersion?.version}`,
      })
    }

    if (inCompetencyTemplatesContext) {
      crumbs.push({
        href: PageUrls.organisationCompetencyTemplates({ organisationId }),
        label: localizedStrings.competenciesTemplates,
      })
    }

    if (competencyTemplate?.title) {
      crumbs.push({
        href: PageUrls.editCompetencyTemplate(
          organisationId,
          competencyTemplateId,
        ),
        label: competencyTemplate.title,
      })
    }

    if (competencyTemplateVersion?.version) {
      crumbs.push({
        href: PageUrls.editCompetencyTemplateVersion({
          orgId: organisationId,
          templateId: competencyTemplateId,
          versionId: competencyTemplateVersionId,
        }),
        label: `V${competencyTemplateVersion.version}`,
      })
    }

    if (inRegisterTemplateContext) {
      crumbs.push({
        href: PageUrls.organisationRegisterTemplates({
          organisationId: organisationId,
        }),
        label: localizedStrings.registerTemplates,
      })
    }

    if (inTaskTemplateContext) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectTaskTemplates({ organisationId, projectId })
          : PageUrls.organisationTaskTemplates({ organisationId }),
        label: localizedStrings.taskTemplates,
      })
    }

    if (taskTemplate?.name) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectTaskTemplates({ organisationId, projectId })
          : PageUrls.organisationTaskTemplates({ organisationId }),
        label: taskTemplate?.name,
      })
    }

    if (inDocumentEngineTemplateContext) {
      crumbs.push({
        href: PageUrls.organisationDocumentEngineTemplates({ organisationId }),
        label: localizedStrings.documentEngineTemplates,
      })
    }

    if (documentEngineTemplate?.name) {
      crumbs.push({
        href: PageUrls.editDocumentEngineTemplate({
          organisationId,
          templateId: documentEngineTemplateId,
        }),
        label: documentEngineTemplate?.name,
      })
    }

    if (inTagsContext) {
      crumbs.push({
        href: PageUrls.organisationProcessTags({
          organisationId: organisationId,
        }),
        label: localizedStrings.tags,
      })
    }

    if (inSiteLocationsContext) {
      crumbs.push({
        href: PageUrls.projectLocations({
          organisationId: organisationId,
          projectId: projectId,
        }),
        label: localizedStrings.siteLocations,
      })
    }

    if (inMapContext) {
      crumbs.push({
        href: PageUrls.projectMapSettings({
          organisationId: organisationId,
          projectId: projectId,
        }),
        label: localizedStrings.map,
      })
    }

    if (inProjectMapContext) {
      crumbs.push({
        href: PageUrls.projectMap({
          organisationId: organisationId,
          projectId: projectId,
        }),
        label: localizedStrings.map,
      })
    }

    if (inPlansContext) {
      crumbs.push({
        href: PageUrls.projectViewPlans(organisationId, projectId),
        label: localizedStrings.plans,
      })
    }

    if (inSiteBoxContext) {
      crumbs.push({
        href: PageUrls.projectSiteBox({
          organisationId: organisationId,
          projectId: projectId,
        }),
        label: localizedStrings.siteBox,
      })
    }

    if (authorisationGroupId && authGroup) {
      crumbs.push({
        href: inProjectContext
          ? PageUrls.projectAuthorisationGroup({
              organisationId: organisationId,
              projectId: projectId,
              groupId: authorisationGroupId,
            })
          : PageUrls.organisationAuthorisationGroup({
              organisationId: organisationId,
              groupId: authorisationGroupId,
            }),
        label: authGroup.name,
      })
    }

    const breadcrumbs = crumbs.map((c, index) => {
      const isLast = index === crumbs.length - 1
      if (isLast || c.disableLink) {
        return <span key={c.href}>{c.label}</span>
      }
      return (
        <Link key={c.href} href={c.href}>
          <MaterialLink href={c.href} underline={'hover'}>
            {c.label}
          </MaterialLink>
        </Link>
      )
    })

    return [breadcrumbs]
  }, [
    inOrganisationHome,
    organisationId,
    inBusinessUnitContext,
    businessUnitId,
    inProjectContext,
    projectId,
    inAnalyticsContext,
    inTimelineContext,
    inTasksContext,
    inObservationsContext,
    taskId,
    inPermitContext,
    permitId,
    inSettingsContext,
    inUsersContext,
    userId,
    inChecklistsContext,
    inInspectionsContext,
    checklistId,
    inRegistersContext,
    inAuthorisationGroupsContext,
    inCompaniesContext,
    inPermitTemplatesContext,
    templateId,
    templateVersionId,
    inChecklistTemplatesContext,
    checklistTemplateId,
    checklistTemplateVersionId,
    inRegisterTemplateContext,
    inTaskTemplateContext,
    inTagsContext,
    inSiteLocationsContext,
    inMapContext,
    inProjectMapContext,
    inPlansContext,
    inSiteBoxContext,
    authorisationGroupId,
    organisation?.name,
    project?.name,
    template?.name,
    permit?.template?.name,
    checklist?.template?.name,
    checklist?.isInspection,
    router.query.isUsedForInspections,
    task?.template?.name,
    taskTemplate?.name,
    businessUnit?.name,
    permitTemplateVersion?.version,
    checklistTemplate?.name,
    checklistTemplateVersion?.version,
    inCompetencyTemplatesContext,
    competencyTemplateId,
    competencyTemplateVersionId,
    competencyTemplate?.title,
    competencyTemplateVersion?.version,
    documentEngineTemplate?.name,
    organisation,
    project,
    authGroup,
    organisationUser,
    companyDetail,
    profile,
  ])

  const baseSx = {
    fontSize: '14px',
    '& .MuiBreadcrumbs-li': {
      fontSize: '14px',
      fontWeight: '500',
      color: 'text.primary',
    },
    '& .MuiBreadcrumbs-li a': {
      color: Palette.Grey[600],
      '&:hover': {
        color: 'primary.main',
        underline: 'none',
        textDecoration: 'none',
      },
    },
  }

  const supportBreadcrumbData = useMemo(() => {
    if (!inSupportContext) return null
    const routes = [
      {
        segment: '/organisations',
        href: PageUrls.adminOrganisations,
        label: localizedStrings.organisations,
      },
      {
        segment: '/users',
        href: PageUrls.adminUsers,
        label: localizedStrings.users,
      },
      {
        segment: '/permits',
        href: PageUrls.adminPermits,
        label: localizedStrings.permits,
      },
      {
        segment: '/analytics',
        href: PageUrls.adminAnalytics,
        label: localizedStrings.analytics,
      },
      {
        segment: '/prodigy-labelling',
        href: PageUrls.supportLabelling,
        label: localizedStrings.Labelling,
      },
      {
        segment: '/mapbox-wsm',
        href: PageUrls.supportMapboxWsm,
        label: localizedStrings.MapboxWsmTest,
      },
      {
        segment: '/kmz-poc',
        href: PageUrls.supportKmzPoc,
        label: localizedStrings.KAZPOC,
      },
      {
        segment: '/kafka',
        href: PageUrls.supportKafka,
        label: localizedStrings.Kafka,
      },
      {
        segment: '/pdf-demo',
        href: PageUrls.pdfDemo,
        label: localizedStrings.PDFdemo,
      },
    ]
    const pathAfterSupport = asPathWithoutQuery.replace(/^\/support/, '')
    const matched = routes.find((r) => pathAfterSupport.startsWith(r.segment))
    const crumbs = matched ? [{ href: matched.href, label: matched.label }] : []
    if (pathAfterSupport.startsWith('/users') && adminUserId) {
      crumbs.push({
        href: `${PageUrls.adminUsers}/${adminUserId}`,
        label: `User ${adminUserId}`,
      })
    }
    return crumbs
  }, [inSupportContext, asPathWithoutQuery, adminUserId])

  if (supportBreadcrumbData) {
    const supportCrumbs = supportBreadcrumbData.map((c, index) => {
      const isLast = index === supportBreadcrumbData.length - 1
      if (isLast) {
        return <span key={c.href}>{c.label}</span>
      }
      return (
        <Link key={c.href} href={c.href}>
          <MaterialLink href={c.href} underline={'hover'}>
            {c.label}
          </MaterialLink>
        </Link>
      )
    })
    return (
      <MaterialBreadcrumbs sx={baseSx}>{supportCrumbs}</MaterialBreadcrumbs>
    )
  }

  return <MaterialBreadcrumbs sx={baseSx}>{breadCrumbs}</MaterialBreadcrumbs>
}
